import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import ClientLogo from '@/components/ClientLogo';
import ClientModal from '@/components/ClientModal';
import Navigation from '@/sections/Navigation';
import Footer from '@/sections/Footer';

interface Client {
  key: number;
  title: string;
  role: string;
  image: string;
  link: string;
  whatWeDid: string;
}

const ClientsList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetch('/clients/clients.json')
      .then((res) => res.json())
      .then((data: Client[]) => {
        setClients(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load clients:', err);
        setLoading(false);
      });
  }, []);

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[80vh] pt-24">
          <div className="text-slate-400">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-12 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Vectoral System</span>
        </button>

        {/* Header */}
        <div className="mb-16">
          <div className="inline-block mb-6">
            <span className="text-xs font-semibold text-kmg-primary uppercase tracking-wider px-3 py-1.5 bg-kmg-primary/10 rounded-full">
              KMG Vectoral System
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            All Clients
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl">
            Explore all the companies powered by the KMG Vectoral System. 
            Each client represents a unique implementation of our infrastructure platform.
          </p>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <button
              key={client.key}
              onClick={() => handleClientClick(client)}
              className="group text-left p-6 bg-white border-2 border-slate-200 rounded-2xl hover:border-kmg-primary hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0">
                  <ClientLogo image={client.image} title={client.title} size="sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-slate-900 mb-1 group-hover:text-kmg-primary transition-colors">
                    {client.title}
                  </h3>
                  <p className="text-sm text-slate-600 font-medium">
                    {client.role}
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                {client.whatWeDid}
              </p>
              <div className="mt-4 flex items-center gap-2 text-kmg-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View Details</span>
                <ExternalLink className="w-4 h-4" />
              </div>
            </button>
          ))}
        </div>
      </main>
      <Footer />
      <ClientModal
        client={selectedClient}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ClientsList;
